import type { RateioCpmLinha, RateioResponse } from '@triade/contracts';
import { minutosParaTexto } from '@/components/centros-custo/tipos';
import { ehZero, formatarCusto, formatarFracaoPercentual, formatarMoeda } from '@/lib/format';
import { TD, TH } from './comum';

/** Rotulo curto do aviso por centro (MINUTOS_ZERO vira "sem minutos"; outros codigos aparecem como vieram). */
function rotuloAviso(codigo: string): string {
  return codigo === 'MINUTOS_ZERO' ? 'sem minutos' : codigo;
}

/** Aba Custo minuto: minutos, MOD e CPM por centro. Sem R$ quando a API nao manda (PCP). */
export function AbaCustoMinuto({ rateio }: { rateio: RateioResponse }) {
  const linhas: RateioCpmLinha[] = rateio.custoPorMinuto;
  const comOciosidade = linhas.some((l) => !ehZero(l.ociosidade));
  const comAcumulado = linhas.some((l) => l.acumulado !== undefined);
  const comMod = linhas.some((l) => l.mod !== undefined);
  const comCpm = linhas.some((l) => l.cpm !== undefined);

  if (linhas.length === 0) {
    return <p role="status" className="px-5 py-8 text-center text-small text-ink-muted">Nenhum centro de transformação com custo por minuto nesta competência.</p>;
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="overflow-x-auto rounded-xl border border-border">
        <table className="w-full min-w-[640px] border-collapse text-small" aria-label="Custo por minuto">
          <thead>
            <tr className="bg-surface-muted text-left text-caption text-ink-muted">
              <th scope="col" className={TH}>Centro de custo</th>
              <th scope="col" className={`${TH} text-right`}>Minutos disponíveis</th>
              {comOciosidade && <th scope="col" className={`${TH} text-right`}>Ociosidade</th>}
              {comOciosidade && <th scope="col" className={`${TH} text-right`}>Minutos efetivos</th>}
              {comAcumulado && <th scope="col" className={`${TH} text-right`}>Acumulado</th>}
              {comMod && <th scope="col" className={`${TH} text-right`}>MOD</th>}
              <th scope="col" className={`${TH} text-right`}>MOD/min</th>
              {comCpm && <th scope="col" className={`${TH} text-right`}>C.P.M.</th>}
            </tr>
          </thead>
          <tbody>
            {linhas.map((l) => (
              <tr key={l.centroId} data-testid="linha-cpm" className="border-t border-line-soft">
                <td className={TD}>
                  <div>
                    <span className="font-medium">{l.codigo}</span> <span className="text-ink-secondary">{l.nome}</span>
                  </div>
                  {l.avisos.map((codigo) => (
                    <span key={codigo} className="mt-1 mr-1 inline-block rounded-full bg-warning-bg px-2 py-0.5 text-caption font-medium text-warning-ink">
                      {rotuloAviso(codigo)}
                    </span>
                  ))}
                </td>
                <td className={`${TD} tabular text-right`}>{minutosParaTexto(l.minutos)}</td>
                {comOciosidade && <td className={`${TD} tabular text-right`}>{formatarFracaoPercentual(l.ociosidade)}</td>}
                {comOciosidade && <td className={`${TD} tabular text-right`}>{minutosParaTexto(l.minutosEfetivos)}</td>}
                {comAcumulado && <td className={`${TD} tabular text-right`}>{formatarMoeda(l.acumulado)}</td>}
                {comMod && <td className={`${TD} tabular text-right`}>{formatarCusto(l.mod)}</td>}
                <td className={`${TD} tabular text-right`}>{formatarCusto(l.modPorMinuto)}</td>
                {comCpm && <td className={`${TD} tabular text-right font-semibold`}>{formatarCusto(l.cpm)}</td>}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="flex flex-wrap items-center justify-end gap-x-6 gap-y-1 px-1 text-small text-ink-secondary" data-testid="rodape-cpm">
        <span>
          Minutos acumulados: <span className="tabular font-semibold text-ink-body">{minutosParaTexto(rateio.minutosAcumulados)}</span>
        </span>
        {rateio.cpmMedio !== undefined && (
          <span>
            CPM médio: <span className="tabular font-semibold text-ink-body">{formatarCusto(rateio.cpmMedio)}</span>
          </span>
        )}
      </div>
    </div>
  );
}
